import { Description } from '@monorepo/shared/componentsV2/Description';
import { ErrorBoundary } from '@monorepo/shared/componentsV2/ErrorBoundary';
import {
  Content,
  Page,
  Title,
} from '@monorepo/shared/componentsV2/fullPageWizard/styled/fullPageWizard';
import { Loading } from '@monorepo/shared/componentsV2/Loading';
import { QueryResultsTable } from '@monorepo/shared/componentsV2/QueryResultsTable';
import { SitePickerForOrg } from '@monorepo/shared/componentsV2/SitePickerForOrg';
import { Toolbar } from '@monorepo/shared/componentsV2/Toolbar';
import { useDisableSubnav } from '@monorepo/shared/hooks/subnav/useDisableSubnav';
import { useQueryResults } from '@monorepo/shared/hooks/useQueryResults';
import { useWorkflow } from '@monorepo/shared/hooks/workflows/useWorkflow';
import {
  contentPadding,
  contentPaddingValue,
  fillHeightAndScrollable,
  verticalLayout,
} from '@monorepo/shared/styles/layout';
import { header4 } from '@monorepo/shared/styles/text';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { EditWorkflowHeader } from '../EditWorkflowHeader';
import { WorkflowIncompleteDataMessages } from '../shared/WorkflowIncompleteDataMessages';
import { WorkflowPreviewEmptyState } from '../shared/WorkflowPreviewEmptyState';
import { WizardStep } from '../types';

const PageContent = styled(Content)`
  padding: 0;
  ${verticalLayout}
`;

const StyledWorkflowIncompleteDataMessages = styled(
  WorkflowIncompleteDataMessages,
)`
  flex-shrink: 0;
  margin: ${contentPaddingValue} ${contentPaddingValue} 0;
`;

const TitleBlock = styled.div`
  ${contentPadding}
`;

const SitePickerActionsGroup = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const PreviewTitle = styled.div`
  ${header4}
`;

const StyledToolbar = styled(Toolbar)`
  background-color: ${({ theme }) => theme.colors.builderBlue08};
  border-top: solid 1px ${({ theme }) => theme.colors.grayddd};
  border-bottom: solid 1px ${({ theme }) => theme.colors.grayddd};
`;

const StyledQueryResultsTable = styled(QueryResultsTable)`
  ${fillHeightAndScrollable}
`;

function WorkflowOutputPreviewTable({
  organizationId,
  projectId,
  originId,
}: {
  organizationId: string;
  projectId: string;
  originId: string;
}) {
  const [page, setPage] = useState(0);
  useEffect(() => setPage(0), [projectId]);

  const {
    isLoading: queryIsLoading,
    isFetching: queryIsFetching,
    queryResult,
  } = useQueryResults({
    originId,
    organizationId,
    projectId,
    query: { steps: [] },
    requestedPage: page,
  });

  return (
    <StyledQueryResultsTable
      isLoading={queryIsLoading || queryIsFetching}
      onPageChange={(newPage) => setPage(newPage)}
      organizationId={organizationId}
      originId={originId}
      projectId={projectId}
      queryResult={queryResult}
    />
  );
}

export function EditWorkflowOutputPreviewPage() {
  const [projectId, setProjectId] = useState<string | undefined>(undefined);
  useDisableSubnav();

  const { organizationId, workflowId } = useParams();
  if (!organizationId || !workflowId) {
    throw new Error(
      "EditWorkflowOutputPreviewPage page was rendered with url that doesn't have organizationId or workflowId",
    );
  }

  const { workflow, isLoading: isWorkflowLoading } = useWorkflow({
    organizationId,
    workflowId,
  });

  if (isWorkflowLoading) {
    return (
      <Page>
        <EditWorkflowHeader wizardStep={WizardStep.PREVIEW} />
        <Loading />
      </Page>
    );
  }

  if (!workflow) {
    throw new Error("Couldn't get information about this workflow.");
  }

  const sitePicker = (
    <SitePickerForOrg
      organizationId={organizationId}
      name="preview-site"
      isLabelHidden
      dontSaveSpaceForErrorText
      isErrorTextHidden
      value={projectId}
      onChange={(e) => setProjectId(e.target.value as string)}
    />
  );

  return (
    <Page>
      <EditWorkflowHeader wizardStep={WizardStep.PREVIEW} />
      <PageContent>
        <StyledWorkflowIncompleteDataMessages workflow={workflow} />
        {projectId ? (
          <>
            <TitleBlock>
              <Title>Preview Workflow</Title>
              <Description>
                This is a preview of the workflow output based on a single
                site’s configuration.
              </Description>
            </TitleBlock>
            <StyledToolbar
              variant="noDropShadow"
              leftActions={
                <SitePickerActionsGroup>
                  <PreviewTitle>Previewing as</PreviewTitle>
                  {sitePicker}
                </SitePickerActionsGroup>
              }
            />
            <ErrorBoundary key={projectId}>
              <WorkflowOutputPreviewTable
                organizationId={organizationId}
                projectId={projectId}
                originId={workflow.outputDataset.id}
              />
            </ErrorBoundary>
          </>
        ) : (
          <WorkflowPreviewEmptyState
            organizationId={organizationId}
            onSelectSite={setProjectId}
          />
        )}
      </PageContent>
    </Page>
  );
}
